import React, { useEffect, useState } from 'react';
import { useUser } from '../context/UserContext';
import axios from 'axios';
import Footer from '../Pages/Footer';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';

/**
 * Retourne un label et une classe CSS selon le statut de la commande
 */
const getStatutBadge = (statut) => {
  const lowerStatut = (statut || '').toLowerCase();

  switch (lowerStatut) {
    case 'en attente':
      return { label: 'En attente', className: 'badge badge-warning' };
    case 'à livrer':
    case 'a livrer':
    case 'préparation':
    case 'en cours':
      return { label: 'À livrer', className: 'badge badge-info' };
    case 'livrée':
    case 'livree':
    case 'livré':
      return { label: 'Livrée', className: 'badge badge-success' };
    case 'annulé':
    case 'annule':
    case 'annuler':
      return { label: 'Annulé', className: 'badge badge-danger' };
    default:
      return { label: 'Inconnu', className: 'badge badge-secondary' };
  }
};

export default function Profil() {
  const { user, setUser } = useUser();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [commandes, setCommandes] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingCmds, setLoadingCmds] = useState(true);

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    toast.info('Déconnecté(e). À bientôt !');
    navigate('/');
  };

  useEffect(() => {
    if (!user?._id) return;

    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/users/${user._id}`
        );
        setProfile(data);
        setUser(data);
      } catch (err) {
        console.error('Erreur chargement profil :', err);
        toast.error('Impossible de récupérer vos informations.');
      } finally {
        setLoadingProfile(false);
      }
    })();
  }, [user?._id, setUser]);

  useEffect(() => {
    if (!user?._id) return;

    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/mes-commandes/${user._id}`
        );
        setCommandes(res.data.commandes);
      } catch (err) {
        toast.error('Erreur lors du chargement des commandes');
      } finally {
        setLoadingCmds(false);
      }
    })();
  }, [user?._id]);

  const handleAnnulation = async (commandeId) => {
    if (!window.confirm('Voulez-vous annuler cette commande ?')) return;

    try {
      await axios.put(
        `${API_URL}/annuler-commande/${commandeId}`
      );
      toast.success('Commande annulée avec succès');
      setCommandes((prev) =>
        prev.map((cmd) =>
          cmd._id === commandeId ? { ...cmd, statut: 'annulé' } : cmd
        )
      );
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l’annulation");
    }
  };

  const renderCommandesList = () => {
    if (loadingCmds) return <p>Chargement…</p>;
    if (commandes.length === 0) return <p>Vous n’avez passé aucune commande.</p>;

    return (
      <div className="mes-commandes-list">
        {commandes.map((commande) => {
          const statut = (commande.statut || commande.status || '').toLowerCase();
          const badge = getStatutBadge(statut);

          return (
            <div key={commande._id} className="commande-tracking-card mb-4 p-3 border rounded">
              <div className="commande-header d-flex justify-content-between align-items-center">
                <div>
                  <h5>
                    Commande #{commande.numeroCommande || commande._id.slice(-5)}{' '}
                    <span className={badge.className}>{badge.label}</span>
                  </h5>
                  <p className="text-muted">
                    📅 {new Date(commande.createdAt).toLocaleDateString()} à{' '}
                    {new Date(commande.createdAt).toLocaleTimeString()}
                  </p>
                </div>

                {/* {statut === 'en attente' && (
                  <button
                    onClick={() => handleAnnulation(commande._id)}
                    className="btn btn-sm btn-danger"
                  >
                    Annuler
                  </button>
                )} */}
              </div>

              <div className="tracking-steps d-flex justify-content-between mt-3 mb-3">
                <div className={`step text-center ${statut === 'en attente' ? 'active' : ''}`}>
                  <span>📦</span>
                  <p>Commande reçue</p>
                </div>
                <div className={`step text-center ${['à livrer', 'a livrer', 'préparation', 'en cours'].includes(statut) ? 'active' : ''}`}>
                  <span>🚚</span>
                  <p>Préparation</p>
                </div>
                <div className={`step text-center ${['livrée', 'livree', 'livré'].includes(statut) ? 'active' : ''}`}>
                  <span>✅</span>
                  <p>Livrée</p>
                </div>
                <div className={`step text-center ${['annulé', 'annule', 'annuler'].includes(statut) ? 'active' : ''}`}>
                  <span>❌</span>
                  <p>Annulée</p>
                </div>
              </div>

              <div>
                <h6>Articles :</h6>
                <ul>
                  {commande.cart.map((item, idx) => (
                    <li key={idx}>
                      {item.title} – {item.quantity} × {item.price} F CFA
                    </li>
                  ))}
                </ul>
                <p className="mt-2 fw-bold">
                  Total : {commande.totalAmount} F CFA
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!user) {
    return <p className="text-center mt-5">Veuillez vous connecter pour voir votre profil.</p>;
  }

  if (loadingProfile) return <p className="text-center mt-5">Chargement…</p>;

  return (
    <>
      <div className="container mt-5">
        <h2>Mon Profil</h2>

        <div className="card p-3 my-4">
          <h4>Mes informations</h4>
          <p><strong>Nom :</strong> {profile?.nom || profile?.name} {profile?.prenom}</p>
          <p><strong>Email :</strong> {profile?.email}</p>
          <p><strong>Numéro :</strong> {profile?.numero || profile?.number}</p>
          <p><strong>Adresse :</strong> {profile?.adresse}, {profile?.ville}</p>
          <button className="btn btn-outline-danger mt-2" onClick={handleLogout}>
            Déconnexion
          </button>
        </div>

        <div className="card p-3">
          <h4>Mes commandes</h4>
          {renderCommandesList()}
        </div>
      </div>
      <Footer />
    </>
  );
}